import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { RadioButton } from "primereact/radiobutton";
import { useEffect, useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import { useGetAllPayersQuery } from "store/queries/payers";
import {
  IPracticeTypeSuggestionRule,
  IPracticeTypeSuggestionRuleSaveRequest,
  PracticeTypeRuleModality,
  PracticeTypeRulePayerType,
} from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

import {
  KEYWORD_CAPTION,
  MIN_KEYWORD_LENGTH,
  MODALITY_CHOICES,
  PAYER_TYPE_CHOICES,
  SERVICE_TYPE_CHOICES,
  SHORT_KEYWORD_PROBLEM,
} from "./serviceTypeRuleWording";

/** The longest reason the API accepts (`PRACTICE_TYPE_SUGGESTION_REASON_MAX_LENGTH`). */
export const REASON_MAX_LENGTH = 500;

type Scope = "ALL" | "ONE";

interface IDraft {
  scope: Scope;
  accountId: string | null;
  modality: PracticeTypeRuleModality | null;
  payerIds: number[];
  payerTypes: PracticeTypeRulePayerType[];
  /** One chip each, as typed. */
  planKeywords: string[];
  excludePlanKeywords: string[];
  targetPracticeTypeCode: string | null;
  acceptedPracticeTypeCodes: string[];
  reason: string;
  enabled: boolean;
  priority: number | null;
}

const toDraft = (
  rule: IPracticeTypeSuggestionRule | null,
  defaultAccountId: string | null
): IDraft => {
  const accountId = rule ? rule.accountId : defaultAccountId;
  return {
    scope: accountId ? "ONE" : "ALL",
    accountId,
    modality: rule?.modality ?? null,
    payerIds: rule?.payerIds ?? [],
    payerTypes: rule?.payerTypes ?? [],
    planKeywords: rule?.planKeywords ?? [],
    excludePlanKeywords: rule?.excludePlanKeywords ?? [],
    targetPracticeTypeCode: rule?.targetPracticeTypeCode ?? null,
    acceptedPracticeTypeCodes: rule?.acceptedPracticeTypeCodes ?? [],
    reason: rule?.reason ?? "",
    enabled: rule?.enabled ?? true,
    priority: rule?.priority ?? null,
  };
};

/** Uppercase, `-`/`_` as spaces, single spaces, de-duplicated — the form the API stores. */
export const normalizeKeywords = (keywords: string[]): string[] => [
  ...new Set(
    keywords
      .map((keyword) =>
        keyword.toUpperCase().replace(/[-_]/g, " ").replace(/\s+/g, " ").trim()
      )
      .filter(Boolean)
  ),
];

/** What is wrong with the draft, in words, or null. The API checks the same things (and answers 400). */
export const draftProblem = (draft: IDraft): string | null => {
  if (draft.scope === "ONE" && !draft.accountId) return "Choose the hospital.";
  if (!draft.modality) return "Choose the procedure type.";
  if (!draft.targetPracticeTypeCode)
    return "Choose the Service Type the check runs under.";
  if (
    [
      ...normalizeKeywords(draft.planKeywords),
      ...normalizeKeywords(draft.excludePlanKeywords),
    ].some((keyword) => keyword.length < MIN_KEYWORD_LENGTH)
  ) {
    return SHORT_KEYWORD_PROBLEM;
  }
  if (draft.reason.trim().length > REASON_MAX_LENGTH) {
    return `Keep the reason under ${REASON_MAX_LENGTH} characters.`;
  }
  return null;
};

export const toSaveRequest = (
  draft: IDraft,
  fallbackPriority: number
): IPracticeTypeSuggestionRuleSaveRequest => ({
  accountId: draft.scope === "ALL" ? null : draft.accountId,
  modality: draft.modality as PracticeTypeRuleModality,
  payerIds: draft.payerIds,
  payerTypes: draft.payerTypes,
  planKeywords: normalizeKeywords(draft.planKeywords),
  excludePlanKeywords: normalizeKeywords(draft.excludePlanKeywords),
  targetPracticeTypeCode: draft.targetPracticeTypeCode as string,
  // The target is never "also accepted" — the API drops it too.
  acceptedPracticeTypeCodes: draft.acceptedPracticeTypeCodes.filter(
    (code) => code !== draft.targetPracticeTypeCode
  ),
  reason: draft.reason.trim() || null,
  enabled: draft.enabled,
  priority: draft.priority ?? fallbackPriority,
});

interface IServiceTypeRuleDialogProps {
  visible: boolean;
  /** The rule being edited, or null when adding. */
  rule: IPracticeTypeSuggestionRule | null;
  /** A NEW rule starts scoped to this hospital (the tab's filter), or to all hospitals when null. */
  defaultAccountId: string | null;
  saving: boolean;
  /** The priority a NEW rule gets when none is typed: past the last rule shown. */
  nextPriority: number;
  onSave: (body: IPracticeTypeSuggestionRuleSaveRequest) => void;
  onHide: () => void;
}

const listOf = (data: any): any[] => (data as any)?.data || data || [];

/**
 * CLE-2971 — Add / Edit a Service Type rule. Mirrors the Payer Auto-Selection dialog: a draft held
 * locally, checked here with the API's own wording before it is sent, and the whole rule sent on save.
 */
export function ServiceTypeRuleDialog({
  visible,
  rule,
  defaultAccountId,
  saving,
  nextPriority,
  onSave,
  onHide,
}: IServiceTypeRuleDialogProps) {
  const [draft, setDraft] = useState<IDraft>(toDraft(rule, defaultAccountId));
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setDraft(toDraft(rule, defaultAccountId));
      setProblem(null);
    }
  }, [visible, rule, defaultAccountId]);

  const { data: payersData } = useGetAllPayersQuery(undefined, {
    skip: !visible,
  });
  const { data: accountsData } = useGetAllAccountsQuery(null, {
    skip: !visible,
  });

  const payerOptions = sortBy(
    listOf(payersData)
      .filter((payer: any) => !payer.archivedAt)
      .map((payer: any) => ({ label: payer.name, value: payer.id })),
    ["label"]
  );
  const accountOptions = sortBy(
    listOf(accountsData).map((account: any) => ({
      label: account.name,
      value: account.id,
    })),
    ["label"]
  );

  const set = (patch: Partial<IDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const save = () => {
    const found = draftProblem(draft);
    setProblem(found);
    if (found) return;
    onSave(toSaveRequest(draft, rule ? rule.priority : nextPriority));
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        type="button"
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        data-testid="serviceTypeRules.dialog.cancel"
      />
      <Button
        type="button"
        label={rule ? "Save rule" : "Add rule"}
        loading={saving}
        onClick={save}
        data-testid="serviceTypeRules.dialog.save"
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={rule ? "Edit Service Type rule" : "Add a Service Type rule"}
      footer={footer}
      style={{ width: "min(680px, 95vw)" }}
      data-testid="serviceTypeRules.dialog"
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-2">
          <Label htmlFor="serviceTypeRules.scope.all">Applies at</Label>
          <div className="flex flex-wrap gap-4">
            <div className="flex align-items-center gap-2">
              <RadioButton
                inputId="serviceTypeRules.scope.all"
                data-testid="serviceTypeRules.scope.all"
                name="serviceTypeRules.scope"
                value="ALL"
                checked={draft.scope === "ALL"}
                onChange={() => set({ scope: "ALL" })}
              />
              <label htmlFor="serviceTypeRules.scope.all">All hospitals</label>
            </div>
            <div className="flex align-items-center gap-2">
              <RadioButton
                inputId="serviceTypeRules.scope.one"
                data-testid="serviceTypeRules.scope.one"
                name="serviceTypeRules.scope"
                value="ONE"
                checked={draft.scope === "ONE"}
                onChange={() => set({ scope: "ONE" })}
              />
              <label htmlFor="serviceTypeRules.scope.one">One hospital</label>
            </div>
          </div>
          {draft.scope === "ONE" && (
            <Dropdown
              inputId="serviceTypeRules.account"
              options={accountOptions}
              value={draft.accountId}
              onChange={(event) => set({ accountId: event.value ?? null })}
              placeholder="Choose a hospital"
              filter
            />
          )}
          <small className="text-color-secondary">
            A hospital&apos;s own enabled rule for a procedure type replaces
            every all-hospitals rule for that procedure type there.
          </small>
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="serviceTypeRules.modality">Procedure type</Label>
          <Dropdown
            inputId="serviceTypeRules.modality"
            options={MODALITY_CHOICES}
            value={draft.modality}
            onChange={(event) => set({ modality: event.value ?? null })}
            placeholder="Choose a procedure type"
          />
          <small className="text-color-secondary">
            {draft.modality === "XRAY"
              ? // CLE-2992 — the API's `orderHasModalityFor`: an X-ray rides along with almost anything.
                "Read from the estimate's CPT codes. An X-ray rule applies only when every code on the estimate is an X-ray."
              : "Read from the estimate's CPT codes."}
          </small>
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="serviceTypeRules.payers">Payers (optional)</Label>
          <MultiSelect
            inputId="serviceTypeRules.payers"
            options={payerOptions}
            value={draft.payerIds}
            onChange={(event) => set({ payerIds: event.value ?? [] })}
            placeholder="Any payer"
            display="chip"
            filter
          />
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="serviceTypeRules.payerTypes">
            Payer types (optional)
          </Label>
          <MultiSelect
            inputId="serviceTypeRules.payerTypes"
            options={PAYER_TYPE_CHOICES}
            value={draft.payerTypes}
            onChange={(event) => set({ payerTypes: event.value ?? [] })}
            placeholder="Any payer type"
            display="chip"
          />
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="serviceTypeRules.planKeywords">
            Plan name contains (optional)
          </Label>
          <Chips
            inputId="serviceTypeRules.planKeywords"
            data-testid="serviceTypeRules.planKeywords"
            value={draft.planKeywords}
            onChange={(event) => set({ planKeywords: event.value ?? [] })}
            separator=","
            addOnBlur
            allowDuplicate={false}
          />
          <small className="text-color-secondary">{KEYWORD_CAPTION}</small>
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="serviceTypeRules.excludePlanKeywords">
            Never when the plan name contains (optional)
          </Label>
          <Chips
            inputId="serviceTypeRules.excludePlanKeywords"
            data-testid="serviceTypeRules.excludePlanKeywords"
            value={draft.excludePlanKeywords}
            onChange={(event) =>
              set({ excludePlanKeywords: event.value ?? [] })
            }
            separator=","
            addOnBlur
            allowDuplicate={false}
          />
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="serviceTypeRules.target">Runs under</Label>
          <Dropdown
            inputId="serviceTypeRules.target"
            options={SERVICE_TYPE_CHOICES}
            value={draft.targetPracticeTypeCode}
            onChange={(event) =>
              set({ targetPracticeTypeCode: event.value ?? null })
            }
            placeholder="Choose a Service Type"
          />
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="serviceTypeRules.accepted">
            Also accepted (optional)
          </Label>
          <MultiSelect
            inputId="serviceTypeRules.accepted"
            options={SERVICE_TYPE_CHOICES.filter(
              (choice) => choice.value !== draft.targetPracticeTypeCode
            )}
            value={draft.acceptedPracticeTypeCodes}
            onChange={(event) =>
              set({ acceptedPracticeTypeCodes: event.value ?? [] })
            }
            placeholder="None"
            display="chip"
          />
          <small className="text-color-secondary">
            A check already run under one of these is left alone — e.g. X-Ray +
            MRI/CAT Scan (46) returns the same MRI/CAT block as 31.
          </small>
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="serviceTypeRules.reason">Reason</Label>
          <InputTextarea
            id="serviceTypeRules.reason"
            data-testid="serviceTypeRules.reason"
            rows={2}
            value={draft.reason}
            onChange={(event) => set({ reason: event.target.value })}
          />
          <small className="text-color-secondary">
            Shown to estimators in the &quot;Service Type changed&quot; notice.
            About the plan, never about a patient.
          </small>
        </div>

        <div className="grid">
          <div className="col-12 md:col-6 flex flex-column gap-1">
            <Label htmlFor="serviceTypeRules.priority">Priority</Label>
            <InputNumber
              inputId="serviceTypeRules.priority"
              data-testid="serviceTypeRules.priority"
              value={draft.priority}
              onValueChange={(event) =>
                set({ priority: (event.value as number | null) ?? null })
              }
              placeholder={String(nextPriority)}
              useGrouping={false}
            />
            <small className="text-color-secondary">
              Lower runs first. Empty = after the rules shown.
            </small>
          </div>
          <div className="col-12 md:col-6 flex align-items-center gap-2">
            <Checkbox
              inputId="serviceTypeRules.enabled"
              checked={draft.enabled}
              onChange={(event) => set({ enabled: !!event.checked })}
            />
            <label htmlFor="serviceTypeRules.enabled">Enabled</label>
          </div>
        </div>

        {problem && (
          <Message
            severity="error"
            text={problem}
            data-testid="serviceTypeRules.dialog.problem"
          />
        )}
      </div>
    </Dialog>
  );
}
